import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Wallet, Percent, Star, RefreshCw, ShoppingCart, TrendingUp,
  ShieldCheck, PackageOpen, ChevronDown, ArrowUpRight,
  Users, Zap, BarChart3, ArrowRight,
} from "lucide-react";
import { PablooLogoWithText } from "../../assets/assets";
import { sendSlackMessage } from "../../models/notifications";
import { readCart, readPurchasedCart } from "../../modules/checkoutHelpers";
import { ShoppingCartIcon } from "@heroicons/react/20/solid";
import { isSessionActive } from "../../modules/sessionCookie";
import PromotionalBanner from "./promotionalBanner";

// ─── nav data ─────────────────────────────────────────────────────────────────

const PRODUCTS = [
  {
    icon:  <Wallet size={18} strokeWidth={1.75} />,
    label: "Store Credit",
    desc:  "Issue and manage store currency that shoppers actually spend.",
    href:  "/products",
  },
  {
    icon:  <Percent size={18} strokeWidth={1.75} />,
    label: "Cashback",
    desc:  "Cash rewards at checkout that bring customers back.",
    href:  "/products/cashback",
  },
  {
    icon:  <Star size={18} strokeWidth={1.75} />,
    label: "Memberships",
    desc:  "Subscription loyalty tiers that compound retention.",
    href:  "/products/memberships",
  },
  {
    icon:  <RefreshCw size={18} strokeWidth={1.75} />,
    label: "Store Credit Refunds",
    desc:  "Turn every return into a retained customer.",
    href:  "/products/store-credit-refunds",
  },
];

const SOLUTIONS = [
  {
    icon:  <ShoppingCart size={18} strokeWidth={1.75} />,
    label: "Cart Abandonment",
    desc:  "Credit incentives that finish the checkout.",
    href:  "/solutions/shopify-cart-abandonment-solutions",
  },
  {
    icon:  <TrendingUp size={18} strokeWidth={1.75} />,
    label: "Cashback vs. Discounts",
    desc:  "Why issuing credit beats cutting prices.",
    href:  "/solutions/shopify/cashback/shopify-cashback-vs-discounts",
  },
  {
    icon:  <ShieldCheck size={18} strokeWidth={1.75} />,
    label: "No More Chargebacks",
    desc:  "Stop disputes before they reach your processor.",
    href:  "/solutions/shopify/no-more-chargebacks",
  },
  {
    icon:  <PackageOpen size={18} strokeWidth={1.75} />,
    label: "Inventory Liquidation",
    desc:  "Move dead stock with credit campaigns instead of markdowns.",
    href:  "/solutions/shopify/inventory-liquidation",
  },
  {
    icon:  <Users size={18} strokeWidth={1.75} />,
    label: "Customer Retention",
    desc:  "Keep your best customers from going quiet.",
    href:  "/solutions",
  },
  {
    icon:  <BarChart3 size={18} strokeWidth={1.75} />,
    label: "Cashback Programs",
    desc:  "End-to-end cashback architecture for Shopify brands.",
    href:  "/solutions/cashback",
  },
];

const MANAGED_POINTS = [
  "Dedicated Pabloo economist, embedded in your brand",
  "Campaign design and execution — fully handled",
  "Quarterly Economy Review delivered to your inbox",
  "Currency health and float monitoring, always on",
  "4 economist hours a month, included",
];

// ─── tokens ────────────────────────────────────────────────────────────────────

const BAR_H          = 72;
const EASE           = [0.22, 1, 0.36, 1];

// ─── sub-components ───────────────────────────────────────────────────────────

function ProductsPanel({ onNavigate }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 0 }}>
      {/* left — product list */}
      <div style={{ padding: "32px 36px" }}>
        <p style={{ fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#a3a3a3", marginBottom: "20px" }}>
          Products
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px" }}>
          {PRODUCTS.map((p) => (
            <Link
              key={p.href}
              to={p.href}
              style={{
                display:        "flex",
                alignItems:     "flex-start",
                gap:            "14px",
                padding:        "16px",
                borderRadius:   "12px",
                background:     "transparent",
                textAlign:      "left",
                transition:     "background 0.15s",
                textDecoration: "none",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#f6f5ef"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              <div style={{ color: "#16a34a", marginTop: "2px", flexShrink: 0 }}>{p.icon}</div>
              <div>
                <div style={{ fontSize: "0.9rem", fontWeight: 600, color: "#1a1a1a", marginBottom: "3px" }}>{p.label}</div>
                <div style={{ fontSize: "0.8125rem", color: "#6b7280", lineHeight: 1.5 }}>{p.desc}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* right — featured */}
      <div
        style={{
          background:   "#f6f5ef",
          borderLeft:   "1px solid rgba(0,0,0,0.06)",
          padding:      "32px 28px",
          display:      "flex",
          flexDirection:"column",
          justifyContent:"space-between",
        }}
      >
        <div>
          <p style={{ fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#a3a3a3", marginBottom: "16px" }}>
            Platform
          </p>
          <p style={{ fontSize: "1.0625rem", fontWeight: 700, color: "#1a1a1a", lineHeight: 1.4, marginBottom: "12px" }}>
            One ledger.<br />Every product.
          </p>
          <p style={{ fontSize: "0.8125rem", color: "#6b7280", lineHeight: 1.6 }}>
            Every Pabloo product runs on the same credit ledger. Switch products,
            stack them, or move tiers — your customer balances follow.
          </p>
        </div>
        <Link
          to="/products"
          style={{
            marginTop:      "24px",
            display:        "inline-flex",
            alignItems:     "center",
            gap:            "6px",
            fontSize:       "0.8125rem",
            fontWeight:     600,
            color:          "#16a34a",
            textDecoration: "none",
            padding:        0,
          }}
        >
          View all products <ArrowRight size={13} />
        </Link>
      </div>
    </div>
  );
}

function SolutionsPanel({ onNavigate }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: 0 }}>
      {/* left — solutions grid */}
      <div style={{ padding: "32px 36px" }}>
        <p style={{ fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#a3a3a3", marginBottom: "20px" }}>
          Solutions
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px" }}>
          {SOLUTIONS.map((s) => (
            <Link
              key={s.href}
              to={s.href}
              style={{
                display:        "flex",
                alignItems:     "flex-start",
                gap:            "14px",
                padding:        "16px",
                borderRadius:   "12px",
                background:     "transparent",
                textAlign:      "left",
                transition:     "background 0.15s",
                textDecoration: "none",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#f6f5ef"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              <div style={{ color: "#16a34a", marginTop: "2px", flexShrink: 0 }}>{s.icon}</div>
              <div>
                <div style={{ fontSize: "0.9rem", fontWeight: 600, color: "#1a1a1a", marginBottom: "3px" }}>{s.label}</div>
                <div style={{ fontSize: "0.8125rem", color: "#6b7280", lineHeight: 1.5 }}>{s.desc}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* right — case studies teaser */}
      <div
        style={{
          background:    "#f6f5ef",
          borderLeft:    "1px solid rgba(0,0,0,0.06)",
          padding:       "32px 28px",
          display:       "flex",
          flexDirection: "column",
          justifyContent:"space-between",
        }}
      >
        <div>
          <p style={{ fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#a3a3a3", marginBottom: "16px" }}>
            Case Studies
          </p>
          <p style={{ fontSize: "1.0625rem", fontWeight: 700, color: "#1a1a1a", lineHeight: 1.4, marginBottom: "12px" }}>
            See how brands build their economies.
          </p>
          <p style={{ fontSize: "0.8125rem", color: "#6b7280", lineHeight: 1.6 }}>
            Real numbers from real merchants who swapped discounts for store credit
            and kept the margin.
          </p>
        </div>
        <Link
          to="/case-study"
          style={{
            marginTop:      "24px",
            display:        "inline-flex",
            alignItems:     "center",
            gap:            "6px",
            fontSize:       "0.8125rem",
            fontWeight:     600,
            color:          "#16a34a",
            textDecoration: "none",
            padding:        0,
          }}
        >
          Read case studies <ArrowRight size={13} />
        </Link>
      </div>
    </div>
  );
}

function ManagedPanel({ onNavigate }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0 }}>
      {/* left — pitch */}
      <div style={{ padding: "40px 44px" }}>
        <p style={{ fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#16a34a", marginBottom: "16px" }}>
          Pabloo Managed Economy
        </p>
        <p style={{ fontSize: "1.5rem", fontWeight: 800, color: "#1a1a1a", lineHeight: 1.25, marginBottom: "16px", letterSpacing: "-0.02em" }}>
          Put an economist<br />behind your brand.
        </p>
        <p style={{ fontSize: "0.9rem", color: "#6b7280", lineHeight: 1.7, marginBottom: "28px", maxWidth: "340px" }}>
          A dedicated Pabloo economist runs inside your brand. They design
          your campaigns, execute them, and keep your economy healthy.
          You set the direction. Everything else gets handled.
        </p>
        <button
          type="button"
          onClick={() => {
            sendSlackMessage("Visitor clicked Managed Economy in nav dropdown.", "#website-updates");
            onNavigate("/bookCall");
          }}
          style={{
            display:      "inline-flex",
            alignItems:   "center",
            gap:          "8px",
            padding:      "12px 24px",
            borderRadius: "99px",
            background:   "#16a34a",
            color:        "#ffffff",
            fontSize:     "0.875rem",
            fontWeight:   700,
            border:       "none",
            cursor:       "pointer",
            transition:   "background 0.15s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "#15803d"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "#16a34a"; }}
        >
          Apply now <ArrowUpRight size={14} strokeWidth={2.5} />
        </button>
      </div>

      {/* right — what's included */}
      <div
        style={{
          background:    "#1a1a1a",
          borderLeft:    "1px solid rgba(0,0,0,0.12)",
          padding:       "40px 40px",
        }}
      >
        <p style={{ fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#4ade80", marginBottom: "24px" }}>
          What's included
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          {MANAGED_POINTS.map((pt, i) => (
            <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
              <div
                style={{
                  width:       "18px",
                  height:      "18px",
                  borderRadius:"50%",
                  background:  "rgba(74,222,128,0.15)",
                  border:      "1px solid rgba(74,222,128,0.3)",
                  display:     "flex",
                  alignItems:  "center",
                  justifyContent:"center",
                  flexShrink:  0,
                  marginTop:   "1px",
                }}
              >
                <div style={{ width: "5px", height: "5px", borderRadius: "50%", background: "#4ade80" }} />
              </div>
              <p style={{ fontSize: "0.875rem", color: "#d4d4d4", lineHeight: 1.55, margin: 0 }}>{pt}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── PrimaryNav (desktop only) ────────────────────────────────────────────────

export default function PrimaryNav({ shouldShowPremiumBanner, theme }) {
  const navigate   = useNavigate();
  const location   = useLocation();
  const [isSignedIn] = useState(() => isSessionActive());

  const dark = theme === "managed";
  const GOLD = "#c9a85c";
  const N = dark ? {
    barBg:       (scrolled) => scrolled ? "rgba(8,8,10,0.97)"  : "rgba(8,8,10,0.85)",
    border:      (scrolled) => scrolled ? "rgba(201,168,92,0.14)" : "transparent",
    shadow:      (scrolled) => scrolled ? "0 4px 32px rgba(0,0,0,0.5)" : "none",
    text:        "#c8bfb0",
    textActive:  "#f5f0e8",
    hoverBg:     "rgba(255,255,255,0.05)",
    accent:      GOLD,
    cta:         GOLD,
    ctaText:     "#08080a",
    ctaHover:    "#b8954a",
    logoFilter:  "brightness(0) invert(1)",
  } : {
    barBg:       (scrolled) => scrolled ? "rgba(255,255,255,0.92)" : "#ffffff",
    border:      (scrolled) => scrolled ? "rgba(0,0,0,0.07)" : "transparent",
    shadow:      (scrolled) => scrolled ? "0 4px 32px rgba(0,0,0,0.07)" : "none",
    text:        "#374151",
    textActive:  "#1a1a1a",
    hoverBg:     "#f6f5ef",
    accent:      "#16a34a",
    cta:         "#16a34a",
    ctaText:     "#ffffff",
    ctaHover:    "#15803d",
    logoFilter:  "none",
  };

  const [scrolled,    setScrolled]    = useState(false);
  const [hidden,      setHidden]      = useState(false);
  const [activeMenu,  setActiveMenu]  = useState(null);
  const [cartVisible, setCartVisible] = useState(false);
  const [hideBanner,  setHideBanner]  = useState(false);

  const lastScrollY  = useRef(0);
  const closeTimer   = useRef(null);

  // ── external scripts ──────────────────────────────────────────────────────
  useEffect(() => {
    const load = (src) => new Promise((res, rej) => {
      const s = document.createElement("script");
      s.src = src; s.async = true;
      s.onload = () => res(s);
      s.onerror = () => rej();
      document.body.appendChild(s);
    });
    load("https://firebasestorage.googleapis.com/v0/b/pabloo-inc.appspot.com/o/website_development%2Ftribe-query.js?alt=media&token=cb0874bb-4c9d-49b9-b392-ea338184edd7")
      .then(() => load("https://firebasestorage.googleapis.com/v0/b/pabloo-inc.appspot.com/o/website_development%2FpablooFlow.js?alt=media&token=61b7ddf9-9928-4a1b-b9da-0a4512a2f909"))
      .catch(console.error);
  }, []);

  // ── scroll: frosted + smart hide ─────────────────────────────────────────
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 12);
      if (y < 80) {
        setHidden(false);
      } else if (y > lastScrollY.current + 6) {
        setHidden(true);
        setActiveMenu(null);
      } else if (y < lastScrollY.current - 6) {
        setHidden(false);
      }
      lastScrollY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // ── cart ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    readCart((c) => {
      const items = c?.productLineItems || [];
      setCartVisible(items.length > 0);
      if (items.length > 0) setHideBanner(true);
    });
    readPurchasedCart((c) => {
      if ((c?.productLineItems || []).length > 0) setHideBanner(true);
    });
  }, []);

  // ── dropdown timing ───────────────────────────────────────────────────────
  const openMenu  = (key) => { clearTimeout(closeTimer.current); setActiveMenu(key); };
  const scheduleClose = () => { closeTimer.current = setTimeout(() => setActiveMenu(null), 130); };

  // ── helpers ───────────────────────────────────────────────────────────────
  const handleSectionClick = (section) => {
    window.dispatchEvent(new CustomEvent("scrollToSection", { detail: { section } }));
    navigate(`/?section=${section}`);
  };

  const handleStartFree = () => {
    sendSlackMessage("Someone has clicked on the 'Start for Free' from the Navigation tab.", "#website-updates");
    window.gtag("event", "conversion_button_clicked", { button: "view_shopify_listing", source: "Navigation button" });
    if (isSignedIn) {
      window.location.href = "https://app.pabloo.com";
    } else {
      window.location.href = "https://app.pabloo.com/sign-up";
    }
  };

  // ─── render ───────────────────────────────────────────────────────────────

  const isActive     = (href) => href && location.pathname === href;
  const isMenuActive = (key) => ({
    products:  ["/products", "/features"],
    solutions: ["/solutions"],
    managed:   ["/bookCall"],
  }[key] || []).some((p) => location.pathname.startsWith(p));

  const MENU_ITEMS = [
    { key: "products",  label: "Products",  panel: <ProductsPanel  onNavigate={(h) => { setActiveMenu(null); navigate(h); }} /> },
    { key: "solutions", label: "Solutions", panel: <SolutionsPanel onNavigate={(h) => { setActiveMenu(null); navigate(h); }} /> },
    { key: "managed",   label: "Managed Economy", panel: <ManagedPanel onNavigate={(h) => { setActiveMenu(null); navigate(h); }} />, accent: true },
  ];

  const SIMPLE_LINKS = [
    { label: "Features",     href: "/features" },
    { label: "Pricing",      href: "/pricing" },
    { label: "Testimonials", onClick: () => handleSectionClick("testimonials") },
  ];

  return (
    <>
      {/* spacer */}
      <div style={{ height: hideBanner ? BAR_H : BAR_H + 40 }} />

      {/* ── fixed bar ───────────────────────────────────────────────────── */}
      <div
        style={{
          position:             "fixed",
          top:                  0,
          left:                 0,
          right:                0,
          zIndex:               1000,
          transform:            hidden ? "translateY(-110%)" : "translateY(0)",
          transition:           "transform 0.35s cubic-bezier(0.22,1,0.36,1), background 0.2s, box-shadow 0.2s, border-color 0.2s",
          background:           N.barBg(scrolled),
          backdropFilter:       scrolled ? "blur(20px)" : "none",
          WebkitBackdropFilter: scrolled ? "blur(20px)" : "none",
          borderBottom:         `1px solid ${N.border(scrolled)}`,
          boxShadow:            N.shadow(scrolled),
        }}
      >
        {/* inner */}
        <div
          style={{
            maxWidth:       "1320px",
            margin:         "0 auto",
            padding:        "0 32px",
            height:         `${BAR_H}px`,
            display:        "flex",
            alignItems:     "center",
            justifyContent: "space-between",
            gap:            "16px",
          }}
        >
          {/* Logo */}
          <a href="/" style={{ flexShrink: 0, display: "flex", alignItems: "center", marginRight: "8px" }}>
            <img src={PablooLogoWithText} alt="Pabloo" style={{ height: "26px", width: "auto", filter: N.logoFilter }} loading="lazy" />
          </a>

          {/* Desktop nav */}
          <nav style={{ display: "flex", alignItems: "center", gap: "2px", flex: 1 }}>
            {/* Dropdown items */}
            {MENU_ITEMS.map((item) => (
              <div
                key={item.key}
                style={{ position: "relative" }}
                onMouseEnter={() => openMenu(item.key)}
                onMouseLeave={scheduleClose}
              >
                <button
                  type="button"
                  style={{
                    display:      "inline-flex",
                    alignItems:   "center",
                    gap:          "4px",
                    padding:      "8px 14px",
                    borderRadius: "8px",
                    background:   (activeMenu === item.key || isMenuActive(item.key))
                      ? (item.accent ? `rgba(${dark ? "201,168,92" : "22,163,74"},0.08)` : N.hoverBg)
                      : "transparent",
                    border:       "none",
                    fontSize:     "0.9rem",
                    fontWeight:   (item.accent || isMenuActive(item.key)) ? 700 : 500,
                    color:        item.accent ? N.accent : ((activeMenu === item.key || isMenuActive(item.key)) ? N.textActive : N.text),
                    cursor:       "pointer",
                    transition:   "background 0.15s, color 0.15s",
                    fontFamily:   "inherit",
                    whiteSpace:   "nowrap",
                  }}
                  onMouseEnter={(e) => {
                    if (!item.accent) { e.currentTarget.style.background = N.hoverBg; e.currentTarget.style.color = N.textActive; }
                  }}
                  onMouseLeave={(e) => {
                    if (!item.accent && activeMenu !== item.key && !isMenuActive(item.key)) {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.color = N.text;
                    }
                  }}
                >
                  {item.label}
                  <ChevronDown
                    size={13}
                    strokeWidth={2.5}
                    style={{
                      transition: "transform 0.2s",
                      transform:  activeMenu === item.key ? "rotate(180deg)" : "none",
                      color:      item.accent ? "#16a34a" : "currentColor",
                      opacity:    0.6,
                    }}
                  />
                </button>
              </div>
            ))}

            {/* Simple links */}
            {SIMPLE_LINKS.map((l) => {
              const active = isActive(l.href);
              const sharedStyle = {
                padding:        "8px 14px",
                borderRadius:   "8px",
                background:     active ? N.hoverBg : "transparent",
                fontSize:       "0.9rem",
                fontWeight:     active ? 600 : 500,
                color:          active ? N.textActive : N.text,
                cursor:         "pointer",
                transition:     "background 0.15s, color 0.15s",
                fontFamily:     "inherit",
                whiteSpace:     "nowrap",
                textDecoration: "none",
                display:        "inline-block",
                border:         "none",
              };
              const hoverOn  = (e) => { e.currentTarget.style.background = N.hoverBg; e.currentTarget.style.color = N.textActive; };
              const hoverOff = (e) => {
                e.currentTarget.style.background = active ? N.hoverBg : "transparent";
                e.currentTarget.style.color = active ? N.textActive : N.text;
              };
              return l.href ? (
                <Link key={l.label} to={l.href} style={sharedStyle} onMouseEnter={hoverOn} onMouseLeave={hoverOff}>
                  {l.label}
                </Link>
              ) : (
                <button key={l.label} type="button" onClick={l.onClick} style={sharedStyle} onMouseEnter={hoverOn} onMouseLeave={hoverOff}>
                  {l.label}
                </button>
              );
            })}
          </nav>

          {/* Right CTAs */}
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
            {cartVisible && (
              <button type="button" onClick={() => navigate("/cart")}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#374151", width: "22px", height: "22px", padding: 0 }}>
                <ShoppingCartIcon />
              </button>
            )}

            {!isSignedIn && (
              <a href="https://app.pabloo.com/sign-in"
                style={{ padding: "8px 14px", fontSize: "0.9rem", fontWeight: 500, color: N.text, fontFamily: "inherit", borderRadius: "8px", transition: "color 0.15s", textDecoration: "none", display: "inline-block" }}
                onMouseEnter={(e) => { e.currentTarget.style.color = N.accent; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = N.text; }}
              >
                Sign in
              </a>
            )}

            <button
              type="button"
              onClick={handleStartFree}
              style={{
                display:      "inline-flex",
                alignItems:   "center",
                gap:          "7px",
                padding:      "9px 20px",
                borderRadius: "99px",
                background:   N.cta,
                color:        N.ctaText,
                fontSize:     "0.875rem",
                fontWeight:   700,
                border:       "none",
                cursor:       "pointer",
                fontFamily:   "inherit",
                whiteSpace:   "nowrap",
                transition:   "background 0.15s",
                letterSpacing:"-0.01em",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = N.ctaHover; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = N.cta; }}
            >
              {isSignedIn ? "Dashboard" : "Start for free"}
              <svg width="8" height="13" viewBox="0 0 8 13" fill="none">
                <path d="M1.5 1.5L6.5 6.5L1.5 11.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>

        </div>

        {/* ── mega menu panel ──────────────────────────────────────────── */}
        <AnimatePresence>
          {activeMenu && (
            <motion.div
              key={activeMenu}
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.18, ease: EASE }}
              onMouseEnter={() => openMenu(activeMenu)}
              onMouseLeave={scheduleClose}
              style={{
                position:    "absolute",
                top:         "100%",
                left:        0,
                right:       0,
                width:       "min(900px, calc(100vw - 48px))",
                marginLeft:  "auto",
                marginRight: "auto",
                background: "#ffffff",
                border:     "1px solid rgba(0,0,0,0.07)",
                borderRadius:"16px",
                boxShadow:  "0 24px 64px rgba(0,0,0,0.13)",
                overflow:   "hidden",
                marginTop:  "8px",
              }}
            >
              {MENU_ITEMS.find((m) => m.key === activeMenu)?.panel}
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── promo banner ─────────────────────────────────────────────── */}
        {!hideBanner && <PromotionalBanner shouldShowPremium={shouldShowPremiumBanner} />}
      </div>

    </>
  );
}
